// src/pages/admin/AdminBookings.jsx
import React, { useState, useEffect, useRef } from 'react';
import AdminNavbar from '../../pages/admin/admin_navbar'; // adjust path if needed
import AdminSidebar from './admin_sidebar'; // adjust path if needed
import SummaryApi from '../../common'; // your SummaryApi file
import { apiCall } from '../../utils/apiService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// Import react-datepicker and its CSS
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

export default function AdminBookings() {
    const initialFormState = {
        patient_name: '',
        mobile_number: '',
        email: '',
        whatsapp_number: '',
        age: '',
        gender: '',
        appointment_date: '',
        appointment_time: '',
        problem_description: '',
        notify_before: 30,
    };

    const [bookings, setBookings] = useState([]); // All appointments
    const [filteredBookings, setFilteredBookings] = useState([]); // Filtered by date
    const [selectedDate, setSelectedDate] = useState(new Date()); // Today by default
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Modal & form states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialFormState);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formMessage, setFormMessage] = useState(null);
    const [formError, setFormError] = useState(null);
    const [dateCounts, setDateCounts] = useState({});

    // Ref to scroll to appointments section
    const appointmentsRef = useRef(null);

    useEffect(() => {
        loadAppointments();
    }, []);

    const loadAppointments = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await apiCall(SummaryApi.getAllAppointment);
            let appointmentsArray = [];
            if (Array.isArray(data)) {
                appointmentsArray = data;
            } else if (data?.status === true && Array.isArray(data.data)) {
                appointmentsArray = data.data;
            } else if (data?.appointments) {
                appointmentsArray = data.appointments;
            }
            setBookings(appointmentsArray);
            filterByDate(appointmentsArray, selectedDate);
            // Compute date counts
            const counts = {};
            appointmentsArray.forEach(b => {
                const date = b.appointment_date;
                if (date) {
                    counts[date] = (counts[date] || 0) + 1;
                }
            });
            setDateCounts(counts);
        } catch (err) {
            setError(err.message || 'Failed to load appointments');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filterByDate = (allBookings, date) => {
        if (!date) {
            setFilteredBookings(allBookings);
            return;
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        const filtered = allBookings.filter(b => b.appointment_date === dateString);
        setFilteredBookings(filtered);
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
        filterByDate(bookings, date);

        // Smooth scroll to appointments section after update
        setTimeout(() => {
            appointmentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
    };

    const handleClearFilter = () => {
        setSelectedDate(null);
        setFilteredBookings(bookings);

        // Optional: scroll to top of list when showing all
        setTimeout(() => {
            appointmentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "mobile_number") {
            if (!/^\d*$/.test(value)) return;
            if (value.length > 10) return;
        }
        if (name === "email") {
            if (/\s/.test(value)) return;
        }
        if (name === "whatsapp_number") {
            if (!/^\d*$/.test(value)) return;
            if (value.length > 10) return;
        }
        setFormData({ ...formData, [name]: value });
    };

    const handleCreateBooking = async (e) => {
        e.preventDefault();
        setFormSubmitting(true);
        setFormError(null);
        try {
            const payload = {
                ...formData,
                age: formData.age ? Number(formData.age) : null,
                notify_before: Number(formData.notify_before || 0),
            };
            const response = await apiCall(SummaryApi.createAppointment, payload);
            if (response?.status === true) {
                toast.success(`Appointment created successfully`, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
                setFormData(initialFormState);
                loadAppointments();
                setTimeout(() => {
                    setIsModalOpen(false);
                }, 500);
            } else {
                throw new Error(response?.message || 'Failed to create appointment');
            }
        } catch (err) {
            console.error(err);
            toast.error(err.message || 'Error creating appointment', {
                position: "top-right",
                autoClose: 4000,
                theme: "colored",
            });
        } finally {
            setFormSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        const s = (status || 'pending').toLowerCase();
        const styles = {
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
            confirmed: 'bg-green-100 text-green-800 border-green-300',
            cancelled: 'bg-red-100 text-red-800 border-red-300',
            completed: 'bg-blue-100 text-blue-800 border-blue-300',
        };
        return (
            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border capitalize ${styles[s] || 'bg-gray-100 text-gray-700 border-gray-300'}`}>
                {s}
            </span>
        );
    };

    // Custom render for day cells — NO weekday name here anymore
    const renderDayContents = (day, date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isPast = date < today;

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const dayStr = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${dayStr}`;
        const count = dateCounts[dateString] || 0;

        return (
            <div className="relative w-full h-full flex flex-col">
                {/* Date number in top-left corner */}
                <div className="absolute top-2 left-2">
                    <span className={`text-xs ${isPast ? 'text-gray-400' : 'text-gray-600'}`}>
                        {day}
                    </span>
                </div>

                {/* Center content - count and appointments text */}
                <div className="flex-1 flex flex-col items-center justify-center mt-6">
                    {!isPast && count > 0 ? (
                        <>
                            <span className="text-3xl font-bold text-[#F97316]">
                                {count}
                            </span>
                            <span className="text-xs font-medium text-[#F97316] mt-1 ">
                                Appointments
                            </span>
                        </>
                    ) : !isPast ? (
                        <div className="flex flex-col items-center">
                            <span className="text-gray-400 text-2xl">—</span>
                            <span className="text-xs text-gray-400 mt-1">
                                No Bookings
                            </span>
                        </div>
                    ) : (
                        <span className="text-sm text-gray-300">—</span>
                    )}
                </div>
            </div>
        );
    };

    // Custom header with weekday names on top
    const renderCustomHeader = ({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
    }) => {
        const monthName = date.toLocaleString('default', { month: 'long' });
        const year = date.getFullYear();

        const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        return (
            <div className="bg-[#0B2A2C] text-white rounded-t-xl p-4">
                <div className="flex justify-between items-center mb-4">
                    <button
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        className="text-white text-2xl disabled:opacity-40"
                    >
                        ←
                    </button>
                    <div className="text-2xl font-semibold">
                        {monthName} {year}
                    </div>
                    <button
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        className="text-white text-2xl disabled:opacity-40"
                    >
                        →
                    </button>
                </div>

                {/* Weekday headers */}
                <div className="grid grid-cols-7 gap-1 text-center font-medium text-lg">
                    {weekdays.map((day) => (
                        <div key={day} className="py-2">
                            {day}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen flex flex-col bg-[#EBE0CF] font-poppins">
            <AdminNavbar />
            <div className="flex flex-1">
                <AdminSidebar />
                <main className="flex-1 p-6 md:p-8 bg-gray-50 overflow-auto">
                    <div className="max-w-7xl mx-auto">
                        {/* Header */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                            <h1 className="text-3xl font-bold text-[#0B2A2C]">All Appointments</h1>
                            <div className="flex flex-wrap items-center gap-4">
                                <button
                                    onClick={loadAppointments}
                                    disabled={loading}
                                    className="px-5 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition"
                                >
                                    {loading ? 'Refreshing...' : 'Refresh'}
                                </button>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="px-6 py-2.5 bg-[#0B2A2C] text-white rounded-lg hover:bg-gray-800 shadow-sm transition"
                                >
                                    + New Appointment
                                </button>
                            </div>
                        </div>

                        {/* Modern Themed Calendar Section - Centered */}
                        <div className="mb-8 bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden flex justify-center">
                            <div className="p-8 w-full max-w-5xl flex flex-col items-center">
                                <div className="w-full flex justify-center">
                                    <DatePicker
                                        selected={selectedDate}
                                        onChange={handleDateChange}
                                        inline
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                        dateFormat="yyyy-MM-dd"
                                        minDate={new Date()}
                                        calendarClassName="!bg-white !border-0 !rounded-xl !shadow-xl !text-[#0B2A2C] !font-poppins"
                                        dayClassName={(date) => {
                                            const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
                                            const isToday = date.toDateString() === new Date().toDateString();
                                            const today = new Date();
                                            today.setHours(0,0,0,0);

                                            let classes = '!w-28 !h-32 !p-0 !rounded-none transition-all duration-200 relative border border-gray-300';

                                            if (isSelected) {
                                                classes += ' !bg-[#0B2A2C] !text-white !font-bold !shadow-md';
                                            } else if (isToday) {
                                                classes += ' !bg-[#E5D8C2] !font-bold';
                                            } else if (date < today) {
                                                classes += ' !text-gray-400 !cursor-not-allowed';
                                            } else {
                                                classes += ' hover:!bg-gray-50';
                                            }

                                            return classes;
                                        }}
                                        renderDayContents={renderDayContents}
                                        renderCustomHeader={renderCustomHeader}
                                        monthClassName="!hidden"           // hide default month/year dropdown row
                                        weekDayClassName={() => "!hidden"} // hide default weekday row (we use custom header)
                                        popperClassName="!z-50"
                                    />
                                </div>

                                {/* Show All Appointments Button */}
                                <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                    <button
                                        onClick={handleClearFilter}
                                        className="px-16 py-4 bg-[#E5D8C2] text-[#0B2A2C] rounded-xl hover:bg-[#0B2A2C] hover:text-[#E5D8C2] transition shadow-md font-semibold text-lg"
                                    >
                                        Show All Appointments
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Appointments list / message - this is what we scroll to */}
                        <div ref={appointmentsRef}>
                            {loading ? (
                                <div className="flex justify-center items-center py-20">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B2A2C] mx-auto mb-4"></div>
                                        <p className="text-gray-600">Loading appointments...</p>
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
                                    <p className="text-red-700 mb-4">Error: {error}</p>
                                    <button
                                        onClick={loadAppointments}
                                        className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            ) : filteredBookings.length === 0 ? (
                                <div className="bg-white rounded-xl shadow p-12 text-center">
                                    <h3 className="text-xl font-semibold text-gray-700 mb-3">
                                        No appointments found {selectedDate ? `on ${selectedDate.toLocaleDateString('en-IN')}` : ''}
                                    </h3>
                                    <p className="text-gray-500 mb-6">Create a new appointment or select another date.</p>
                                    <button
                                        onClick={() => setIsModalOpen(true)}
                                        className="inline-block px-8 py-3 bg-[#0B2A2C] text-white rounded-lg hover:bg-gray-800 transition"
                                    >
                                        Create Appointment
                                    </button>
                                </div>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm bg-white">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-[#0B2A2C] text-white">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold">Patient</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold">Mobile</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold">Date</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold">Time</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold">Concern</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold">Created</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {filteredBookings.map((b, index) => (
                                                <tr key={b.appointment_id} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 font-medium text-gray-900">{index + 1}</td>
                                                    <td className="px-6 py-4 font-medium text-gray-900">{b.patient_name}</td>
                                                    <td className="px-6 py-4 text-gray-700">{b.mobile_number || '—'}</td>
                                                    <td className="px-6 py-4 text-gray-700">{b.appointment_date || '—'}</td>
                                                    <td className="px-6 py-4 text-gray-700">{b.appointment_time?.slice(0,5) || '—'}</td>
                                                    <td className="px-6 py-4 max-w-xs truncate text-gray-600" title={b.problem_description || ''}>
                                                        {b.problem_description || '—'}
                                                    </td>
                                                    <td className="px-6 py-4">{getStatusBadge(b.status)}</td>
                                                    <td className="px-6 py-4 text-gray-500 text-sm">
                                                        {b.created_date
                                                            ? new Date(b.created_date).toLocaleDateString('en-IN')
                                                            : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>

            {/* Create Appointment Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-2xl font-bold text-[#0B2A2C]">Create New Appointment</h2>
                        </div>
                        <form onSubmit={handleCreateBooking} className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Patient Name <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="patient_name"
                                        value={formData.patient_name}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Mobile Number <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="tel"
                                        name="mobile_number"
                                        value={formData.mobile_number}
                                        onChange={handleInputChange}
                                        required
                                        pattern="[0-9]{10}"
                                        maxLength={10}
                                        title="Please enter a 10-digit mobile number"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                                        title="Please enter a valid email address"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number</label>
                                    <input
                                        type="tel"
                                        name="whatsapp_number"
                                        value={formData.whatsapp_number}
                                        onChange={handleInputChange}
                                        pattern="[0-9]{10}"
                                        maxLength={10}
                                        title="Please enter a 10-digit WhatsApp number"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Age <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="age"
                                        value={formData.age}
                                        onChange={handleInputChange}
                                        required
                                        min="1"
                                        max="120"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Appointment Date <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="appointment_date"
                                        value={formData.appointment_date}
                                        onChange={handleInputChange}
                                        required
                                        min={new Date().toISOString().split('T')[0]}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Appointment Time <span className="text-red-600">*</span>
                                    </label>
                                    <input
                                        type="time"
                                        name="appointment_time"
                                        value={formData.appointment_time}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Notify Before (min)</label>
                                    <input
                                        type="number"
                                        name="notify_before"
                                        value={formData.notify_before}
                                        onChange={handleInputChange}
                                        min="0"
                                        step="5"
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Gender <span className="text-red-600">*</span>
                                </label>
                                <select
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]"
                                >
                                    <option value="">Select gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Problem / Description</label>
                                <textarea
                                    name="problem_description"
                                    value={formData.problem_description}
                                    onChange={handleInputChange}
                                    rows={3}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B2A2C]"
                                    placeholder="Main concern, symptoms, medical history..."
                                />
                            </div>

                            {formError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                                    {formError}
                                </div>
                            )}

                            <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    disabled={formSubmitting}
                                    className="px-6 py-2.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={formSubmitting}
                                    className="px-8 py-2.5 bg-[#0B2A2C] text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-60 flex items-center gap-2"
                                >
                                    {formSubmitting ? (
                                        <>
                                            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                                            Creating...
                                        </>
                                    ) : (
                                        'Create Appointment'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ToastContainer />
        </div>
    );
}